/**
 * EnrollGuestDialog — enroll a participant into a session this week.
 *
 * Reworked for speed: pick a session, then choose WHO to enroll via a
 * Guest / Staff searchable list, or scan their wristband to jump straight to
 * them. Scanning reuses `useScanBandOnce`, which flips the NFC connector to
 * "busy" so the always-on global scan popover doesn't steal the tap; the busy
 * flag is released on resolve/timeout/cancel (we also cancel on close).
 * Enrolls via the real mutation; a full session enrolls onto the waitlist.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Nfc, Search, User, Users, X } from 'lucide-react';

import { useTranslation } from '@/core';
import { cn } from '@/lib/utils';
import { notify } from '@/shared/services';
import { useFormattedDate } from '@/shared/hooks';
import { useScanBandOnce } from '@/shared/hooks/useScanBandOnce';
import { useNfcStatus } from '@/shared/hooks/useNfcStatus';
import { lookupWristband } from '@/shared/services/wristbandService';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/theme/components/ui/dialog';
import { Button } from '@/theme/components/ui/button';
import { Input } from '@/theme/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/theme/components/ui/select';
import { useCreateEnrollment } from '@/modules/activities/hooks/useEnrollments';
import type { Activity } from '@/modules/activities/types';
import type { Staff } from '@/modules/staff';
/** Format a `HH:MM:SS` (or `HH:MM`) time string down to `HH:MM`. */
function hhmm(time: string | undefined): string {
  if (!time) return '';
  return time.slice(0, 5);
}

/** First-two-initials from a name, uppercased; em dash when empty. */
function initials(name: string | null | undefined): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '—';
}

/** An in-house guest that can be enrolled (already loaded for the week). */
export interface EnrollGuestOption {
  guestId: string;
  personId: string | null;
  name: string;
  bookingId: string | null;
}

interface EnrollGuestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activities: Activity[];
  /** In-house guests this week — the guest search pool (no extra fetch). */
  inHouseGuests: EnrollGuestOption[];
  staff: Staff[];
  /** Preselect a guest when opened from the guest-stay drawer. */
  initialGuestId?: string | null;
  initialGuestName?: string | null;
  initialBookingId?: string | null;
}

type Mode = 'guest' | 'staff';

export function EnrollGuestDialog({
  open, onOpenChange, activities, inHouseGuests, staff,
  initialGuestId, initialGuestName, initialBookingId,
}: EnrollGuestDialogProps) {
  const { t } = useTranslation();
  const { format } = useFormattedDate();
  const createEnrollment = useCreateEnrollment();
  const { scan, scanning, cancel } = useScanBandOnce();
  const nfcState = useNfcStatus();
  const nfcReady = nfcState === 'ready' || nfcState === 'degraded';
  const nfcHint = nfcState === 'disconnected' || nfcState === 'connecting'
    ? t('frontdesk.inhouse.enroll.scanNoBridge', 'Shaka Bridge is not connected')
    : nfcState === 'no_reader'
      ? t('frontdesk.inhouse.enroll.scanNoReader', 'No NFC reader detected')
      : undefined;

  const [activityId, setActivityId] = useState('');
  const [mode, setMode] = useState<Mode>('guest');
  const [guestId, setGuestId] = useState<string | null>(null);
  const [staffId, setStaffId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [scanMsg, setScanMsg] = useState<string | null>(null);

  // Seed / reset when (re)opened.
  useEffect(() => {
    if (!open) return;
    setActivityId('');
    setMode('guest');
    setGuestId(initialGuestId ?? null);
    setStaffId(null);
    setQuery('');
    setScanMsg(null);
  }, [open, initialGuestId]);

  const close = (o: boolean) => {
    if (!o) cancel(); // release the NFC busy lock if a scan was pending
    onOpenChange(o);
  };

  const sessions = useMemo(
    () => [...activities].sort((a, b) => `${a.date}${a.startTime}`.localeCompare(`${b.date}${b.startTime}`)),
    [activities],
  );
  const selected = sessions.find((a) => a.id === activityId);
  const isFull =
    !!selected && selected.effectiveMaxParticipants != null && selected.enrolledCount >= selected.effectiveMaxParticipants;

  const q = query.trim().toLowerCase();
  const guestMatches = useMemo(() => {
    const pool = inHouseGuests;
    return (q ? pool.filter((g) => g.name.toLowerCase().includes(q)) : pool).slice(0, 40);
  }, [inHouseGuests, q]);
  const staffMatches = useMemo(() => {
    return (q ? staff.filter((s) => s.name.toLowerCase().includes(q)) : staff).slice(0, 40);
  }, [staff, q]);

  const selectedGuest = inHouseGuests.find((g) => g.guestId === guestId);
  const selectedStaff = staff.find((s) => s.id === staffId);

  async function handleScan() {
    setScanMsg(null);
    const uid = await scan();
    if (!uid) { setScanMsg(t('frontdesk.inhouse.enroll.scanNone', 'No card read. Try again.')); return; }
    try {
      const res = await lookupWristband(uid);
      const personId = res.wristband.personId;
      // Prefer the reported role, fall back to matching the personId.
      const guestHit = inHouseGuests.find((g) => g.personId && g.personId === personId);
      const staffHit = staff.find((s) => s.person?.id === personId);
      if ((res.role === 'staff' || (!guestHit && staffHit)) && staffHit) {
        setMode('staff'); setStaffId(staffHit.id); setGuestId(null);
        setScanMsg(t('frontdesk.inhouse.enroll.scanStaff', 'Matched staff: {{name}}', { name: staffHit.name }));
      } else if (guestHit) {
        setMode('guest'); setGuestId(guestHit.guestId); setStaffId(null);
        setScanMsg(t('frontdesk.inhouse.enroll.scanGuest', 'Matched guest: {{name}}', { name: guestHit.name }));
      } else {
        setScanMsg(t('frontdesk.inhouse.enroll.scanUnmatched', 'Wristband not linked to anyone in-house this week.'));
      }
    } catch {
      setScanMsg(t('frontdesk.inhouse.enroll.scanFail', 'Could not look up that wristband.'));
    }
  }

  const canSubmit = !!activityId && (mode === 'guest' ? !!guestId : !!staffId) && !createEnrollment.isPending;

  const submit = () => {
    if (!activityId) return;
    const bookingId = mode === 'guest' ? (selectedGuest?.bookingId ?? initialBookingId ?? undefined) : undefined;
    createEnrollment.mutate(
      {
        activityId,
        data: {
          activityId,
          guestId: mode === 'guest' ? guestId ?? undefined : undefined,
          staffId: mode === 'staff' ? staffId ?? undefined : undefined,
          bookingId,
          waitlistIfFull: isFull,
        },
      },
      { onSuccess: () => close(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('frontdesk.inhouse.enroll.title', 'Enroll in a session')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3.5 py-1">
          {/* session */}
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
              {t('frontdesk.inhouse.enroll.session', 'Session')}
            </label>
            <Select value={activityId} onValueChange={setActivityId}>
              <SelectTrigger>
                <SelectValue placeholder={t('frontdesk.inhouse.enroll.pick', 'Pick a session…')} />
              </SelectTrigger>
              <SelectContent>
                {sessions.map((a) => {
                  const cap = a.effectiveMaxParticipants;
                  return (
                    <SelectItem key={a.id} value={a.id}>
                      {format(`${a.date}T00:00:00`, 'EEE, MMM d')} · {hhmm(a.startTime)} — {a.templateName}
                      {cap != null ? ` (${a.enrolledCount}/${cap})` : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* who — guest / staff tabs + scan */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                {t('frontdesk.inhouse.enroll.who', 'Participant')}
              </label>
              {scanning ? (
                <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-destructive" onClick={() => { cancel(); setScanMsg(null); }}>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t('frontdesk.inhouse.enroll.scanning', 'Tap a band…')}
                  <X className="h-3 w-3" />
                </Button>
              ) : (
                <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5" onClick={handleScan} disabled={!nfcReady} title={nfcHint}>
                  <Nfc className="h-3.5 w-3.5" />
                  {t('frontdesk.inhouse.enroll.scan', 'Scan wristband')}
                </Button>
              )}
            </div>

            <div className="mb-2 inline-flex rounded-full border border-sand-200 bg-card p-0.5">
              {(['guest', 'staff'] as Mode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setMode(m); setQuery(''); }}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-medium transition-colors',
                    mode === m ? 'bg-coral-500 text-white' : 'text-sand-700 hover:text-sand-900',
                  )}
                >
                  {m === 'guest' ? <Users className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                  {m === 'guest' ? t('frontdesk.inhouse.enroll.guest', 'Guest') : t('frontdesk.inhouse.enroll.staff', 'Staff')}
                </button>
              ))}
            </div>

            {scanMsg ? (
              <div className="mb-2 rounded-md border border-ocean-200 bg-ocean-50 px-2.5 py-1.5 text-[11.5px] text-ocean-800 dark:bg-ocean-500/10">
                {scanMsg}
              </div>
            ) : null}

            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={mode === 'guest'
                  ? t('frontdesk.inhouse.enroll.searchGuest', 'Search in-house guests…')
                  : t('frontdesk.inhouse.enroll.searchStaff', 'Search staff…')}
                className="pl-8"
              />
            </div>

            <div className="mt-1.5 max-h-44 overflow-y-auto rounded-md border border-border">
              {mode === 'guest' ? (
                guestMatches.length === 0 ? (
                  <p className="px-3 py-3 text-center text-[12px] text-muted-foreground">
                    {t('frontdesk.inhouse.enroll.noGuests', 'No in-house guests match.')}
                  </p>
                ) : guestMatches.map((g) => (
                  <Row
                    key={g.guestId}
                    name={g.name}
                    selected={g.guestId === guestId}
                    onClick={() => setGuestId(g.guestId)}
                  />
                ))
              ) : (
                staffMatches.length === 0 ? (
                  <p className="px-3 py-3 text-center text-[12px] text-muted-foreground">
                    {t('frontdesk.inhouse.enroll.noStaff', 'No staff match.')}
                  </p>
                ) : staffMatches.map((s) => (
                  <Row
                    key={s.id}
                    name={s.name}
                    selected={s.id === staffId}
                    onClick={() => setStaffId(s.id)}
                  />
                ))
              )}
            </div>
          </div>

          {isFull ? (
            <div className="rounded-md border border-sun-300 bg-sun-50 px-3 py-2 text-[12px] text-sun-900">
              {t('frontdesk.inhouse.enroll.willWaitlist', 'This session is full — the participant will be added to the waitlist.')}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {createEnrollment.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {(() => {
              const who = mode === 'guest' ? selectedGuest?.name ?? initialGuestName : selectedStaff?.name;
              const verb = isFull
                ? t('frontdesk.inhouse.enroll.waitlist', 'Add to waitlist')
                : t('frontdesk.inhouse.enroll.confirm', 'Enroll');
              return who ? `${verb} · ${who}` : verb;
            })()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ name, selected, onClick }: { name: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 border-b border-border px-2.5 py-1.5 text-left last:border-b-0',
        selected ? 'bg-coral-50 dark:bg-coral-500/10' : 'hover:bg-sand-50',
      )}
    >
      <span className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full border border-ocean-200 bg-ocean-100 text-[9px] font-bold text-ocean-700">
        {initials(name)}
      </span>
      <span className="truncate text-[12.5px] font-medium text-sand-900">{name}</span>
      {selected ? <span className="ml-auto text-[11px] font-bold text-coral-600">✓</span> : null}
    </button>
  );
}
